// `ItemList` JSON-LD — Schema.org collection type that makes a listing
// page eligible for Google's CAROUSEL rich result (a horizontal strip of
// 3-10 cards under the SERP entry). Used on collection pages where the
// page itself is a list of links: /markets, /social, /launchpad, etc.
//
// Two emit flavours:
//   - `buildItemListJsonLd()` — pure ListItem entries pointing at URLs.
//     Each item is a position + name + url. Cheap; the linked page's
//     own JSON-LD does the heavy lifting at click-through time.
//   - `buildItemListWithItemsJsonLd()` — same, but EMBEDS each item's
//     full schema (the linked page's surface JSON-LD) inline. More
//     verbose but gives Google everything it needs without a follow-up
//     fetch — better for pages where bot crawl budget matters.
//
// Pages are responsible for capping list length. Schema-spec doesn't
// limit `numberOfItems`, but Google's carousel renders at most ~10 and
// payload >50KB is rejected by the rich-results test. Keep top-N tight.

import { resolveCanonicalOrigin } from '../origin';

import type { JsonLdNode, JsonLdPayload } from './types';

export interface ItemListEntry {
  /** Human-readable label rendered by Google (market title, user name). */
  name: string;
  /** Absolute or relative path to the listed item. Relative paths are
   *  resolved against the canonical origin. */
  url: string;
  /** Optional thumbnail — Google uses this for carousel cards. */
  image?: string;
  /** Optional 1-line description. Surfaces in the carousel snippet. */
  description?: string;
}

export interface BuildItemListOptions {
  /** Schema.org `name` for the list itself, e.g. "Top opinion markets". */
  name: string;
  /** Optional 1-paragraph description of the list. */
  description?: string;
  /** The list entries in display order. Position is 1-indexed automatically. */
  items: readonly ItemListEntry[];
  /** Page URL the list lives on — used as `@id`. */
  pageUrl: string;
}

function normaliseItemUrl(url: string, origin: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return new URL(url.startsWith('/') ? url : `/${url}`, origin).toString();
}

export function buildItemListJsonLd(opts: BuildItemListOptions): JsonLdPayload {
  if (opts.items.length === 0) return null;
  const origin = resolveCanonicalOrigin();
  const node: JsonLdNode = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${opts.pageUrl}#itemlist`,
    name: opts.name,
    ...(opts.description !== undefined ? { description: opts.description } : {}),
    numberOfItems: opts.items.length,
    itemListOrder: 'https://schema.org/ItemListOrderDescending',
    itemListElement: opts.items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      url: normaliseItemUrl(item.url, origin),
      name: item.name,
      ...(item.image !== undefined ? { image: item.image } : {}),
      ...(item.description !== undefined ? { description: item.description } : {}),
    })),
  };
  return node;
}
